import { Pair } from 'bitecs';
import { Output, Mp4OutputFormat, BufferTarget, CanvasSource } from 'mediabunny';
import { Sequence, SequenceState, Tween, TweenState, ChildOf, type State } from '@multiplekex/shallot';
import { StepTransition } from '@blog-components/sequence';
import type { RecordingConfig } from './types';

export function setCanvasSize(canvas: HTMLCanvasElement, width: number, height: number): void {
  const dpr = window.devicePixelRatio || 1;
  canvas.style.width = `${width / dpr}px`;
  canvas.style.height = `${height / dpr}px`;
  canvas.width = width;
  canvas.height = height;
}

export function findSequenceForTransition(state: State, from: number, to: number): number | null {
  for (const eid of state.query([Sequence, StepTransition])) {
    if (StepTransition.from[eid] === from && StepTransition.to[eid] === to) {
      return eid;
    }
  }
  return null;
}

export function getMaxStep(state: State): number {
  let max = 0;
  for (const eid of state.query([Sequence, StepTransition])) {
    max = Math.max(max, StepTransition.from[eid], StepTransition.to[eid]);
  }
  return max;
}

export async function encodeSequence(
  canvas: HTMLCanvasElement,
  state: State,
  seqEid: number,
  config: RecordingConfig,
  onProgress?: (frame: number, total: number) => void
): Promise<Blob> {
  // Reset sequence to initial state
  Sequence.state[seqEid] = SequenceState.IDLE;
  Sequence.elapsed[seqEid] = 0;
  for (const childEid of state.query([Pair(ChildOf.relation, seqEid), Tween])) {
    Tween.state[childEid] = TweenState.IDLE;
    Tween.elapsed[childEid] = 0;
  }

  // Start playing and trigger computeTweenDelays()
  Sequence.state[seqEid] = SequenceState.PLAYING;
  state.step(0);

  // Calculate duration from delay + duration of all tweens
  let maxEndTime = 0;
  for (const childEid of state.query([Pair(ChildOf.relation, seqEid), Tween])) {
    const endTime = (Tween.delay[childEid] ?? 0) + (Tween.duration[childEid] ?? 0);
    maxEndTime = Math.max(maxEndTime, endTime);
  }
  const duration = maxEndTime + 0.5;
  const totalFrames = Math.ceil(duration * config.fps);

  // Setup video output
  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
    target: new BufferTarget(),
  });

  const videoSource = new CanvasSource(canvas, {
    codec: 'avc',
    bitrate: config.bitrate,
  });

  output.addVideoTrack(videoSource, { frameRate: config.fps });
  await output.start();

  const stepTime = 1 / config.fps;

  // Encoding loop
  for (let i = 0; i < totalFrames; i++) {
    setCanvasSize(canvas, config.width, config.height);
    state.step(stepTime);

    const timestamp = i / config.fps;
    await videoSource.add(timestamp, stepTime);

    if (i % 30 === 0) {
      onProgress?.(i, totalFrames);
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  await output.finalize();

  // Mark sequence as complete to prevent replay
  Sequence.state[seqEid] = SequenceState.COMPLETE;
  for (const childEid of state.query([Pair(ChildOf.relation, seqEid), Tween])) {
    Tween.state[childEid] = TweenState.COMPLETE;
  }

  return new Blob([output.target.buffer!], { type: 'video/mp4' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
